/**
 * @fileoverview Account creation step component for the onboarding flow.
 * Provides email/password registration and Google OAuth sign-up options.
 * @module components/onboarding/steps/AccountCreation
 */

"use client";

import { Trans } from "@lingui/react/macro";
import { EmailPasswordForm } from "../components/EmailPasswordForm";
import { GoogleButton } from "../components/GoogleButton";
import { MagicLinkSent } from "../components/MagicLinkSent";
import { SocialProof } from "../components/SocialProof";
import { AccountCreationHeader } from "./AccountCreationHeader";
import { useAccountCreation } from "./useAccountCreation";

/**
 * Account creation form component for new user registration.
 *
 * @description This component is the second step in the signup flow (after plan selection).
 * It provides two methods for account creation:
 *
 * 1. Google OAuth - Quick signup via "Continue with Google" button
 * 2. Email/Password - Traditional form with validation
 *
 * @returns Account creation form React element
 */
export function AccountCreation() {
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    authMethod,
    setAuthMethod,
    isLoading,
    magicLinkSent,
    isResending,
    hasEmailError,
    hasPasswordError,
    honeypotWebsite,
    setHoneypotWebsite,
    honeypotCompany,
    setHoneypotCompany,
    canSubmit,
    handleGoogleSignIn,
    handlePasswordSignUp,
    handleEmailSignUp,
    handleResendMagicLink,
    handleBackToSignup,
    actions,
  } = useAccountCreation();

  // Show magic link sent confirmation screen
  if (magicLinkSent) {
    return (
      <MagicLinkSent
        email={email}
        isResending={isResending}
        onBack={handleBackToSignup}
        onResend={handleResendMagicLink}
      />
    );
  }

  return (
    <div className="flex flex-col items-center">
      <AccountCreationHeader />

      <div className="w-full max-w-md space-y-6">
        <GoogleButton isLoading={isLoading} onClick={handleGoogleSignIn} />

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-gray-200 border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-3 text-gray-500"><Trans>Or continue with email</Trans></span>
          </div>
        </div>

        <EmailPasswordForm
          authMethod={authMethod}
          canSubmit={canSubmit}
          email={email}
          handleSubmit={authMethod === "password" ? handlePasswordSignUp : handleEmailSignUp}
          hasEmailError={hasEmailError}
          hasPasswordError={hasPasswordError}
          honeypotCompany={honeypotCompany}
          honeypotWebsite={honeypotWebsite}
          isLoading={isLoading}
          password={password}
          setAuthMethod={setAuthMethod}
          setEmail={setEmail}
          setHoneypotCompany={setHoneypotCompany}
          setHoneypotWebsite={setHoneypotWebsite}
          setPassword={setPassword}
          setShowPassword={setShowPassword}
          showPassword={showPassword}
        />

        <SocialProof onSignIn={() => actions.setAuthMode("signin")} />
      </div>
    </div>
  );
}
